<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";

import { scrollToHome } from "@/common/utils/common";
import { toast } from "@/common/utils/toast";

import { analysisShow } from "@/common/api/rest/analysis/analysis";

import type { AnalysisType, AnalysisListType } from "@/common/types/analysis";
import type { CompanyType } from "@/common/types/company";

import { baseAccordion, svgIcon } from "@/common/components";
import {
  analysisAnalyse,
  analysisSignature,
  analysisTabEquipment,
  analysisTabEmployee,
  analysisTabForm,
  analysisTabMethod,
  analysisTabResult,
  analysisTabSample,
  analysisTabQuestions,
  analysisTabReport,
} from "@/components";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: AnalysisListType;
    company: CompanyType | null;
    evaluationCode: string | null;
    addressId: string | null;
    width?: string;
    isEdit?: boolean;
  }>(),
  {
    width: "100%",
    isEdit: true,
  },
);

const analysis = ref<AnalysisType | null>(null);

const accordionsOpen = ref<boolean[]>([true]);

const accordions = computed(() => {
  const list = [
    {
      key: "analyse",
      title: t("common.labels.analyse").toUpperCase(),
      icon: "settings",
      component: analysisAnalyse,
      props: {
        data: analysis.value,
      },
    },
    {
      key: "questions",
      title: t("common.labels.questions").toUpperCase(),
      icon: "form",
      component: analysisTabQuestions,
      props: {
        data: analysis.value,
      },
    },
    {
      key: "form",
      title: t("common.labels.form").toUpperCase(),
      icon: "document",
      component: analysisTabForm,
      props: {
        data: analysis.value,
      },
    },
    {
      key: "method",
      title: t("common.labels.method").toUpperCase(),
      icon: "method",
      component: analysisTabMethod,
      props: {
        data: analysis.value,
      },
    },
    {
      key: "employee",
      title: t("common.labels.employee").toUpperCase(),
      icon: "card",
      component: analysisTabEmployee,
      props: {
        company: props.company,
        data: analysis.value,
      },
    },
    {
      key: "equipment",
      title: t("common.labels.equipment").toUpperCase(),
      icon: "equipment",
      component: analysisTabEquipment,
      props: {
        data: analysis.value,
      },
    },
    {
      key: "sample",
      title: t("common.labels.sample").toUpperCase(),
      icon: "samples",
      component: analysisTabSample,
      props: {
        data: analysis.value,
      },
    },
    {
      key: "result",
      title: t("common.labels.results").toUpperCase(),
      icon: "document",
      component: analysisTabResult,
      props: {
        data: analysis.value,
      },
    },
    {
      key: "signature",
      title: t("common.labels.subscriptions").toUpperCase(),
      icon: "signature",
      component: analysisSignature,
      props: {
        data: analysis.value,
        accountId: analysis.value?.account_id,
        analysisId: analysis.value?.analysis_id,
      },
    },
    {
      key: "report",
      title: t("report.titles.field_memory").toUpperCase(),
      icon: "reports",
      component: analysisTabReport,
      props: {
        analysisId: analysis.value?.analysis_id,
      },
    },
  ];

  if (!props.isEdit) {
    return list
      .map((item) => {
        if (item.key === "method")
          return {
            ...item,
            props: {
              ...item.props,
              isEdit: false,
            },
          };

        return item;
      })
      .filter((item) => item.key !== "form" && item.key !== "result");
  }

  return list;
});

function fetchData() {
  if (!props.data) return;

  analysisShow(props.data.account_id, props.data.analysis_id)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        analysis.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function toggleAccordion(index: number) {
  accordions.value.forEach((_, idx) => {
    accordionsOpen.value[idx] = index === idx;
  });

  scrollToHome(accordions.value[index].key);
  fetchData();
}

function update() {
  emit("update-data");
  fetchData();
}

onUnmounted(() => {
  const content = document.getElementsByClassName(
    "evaluation-content",
  )[0] as any;

  if (content) {
    content.style.paddingBlock = "1rem";
    content.style.paddingInline = "1.5rem";
  }
});

onMounted(() => {
  fetchData();

  const content = document.getElementsByClassName(
    "evaluation-content",
  )[0] as any;

  if (content) {
    content.style.paddingBlock = 0;
    content.style.paddingInline = 0;
  }
});
</script>

<template>
  <section class="tab-component" :class="{ width: width }">
    <div class="content">
      <template v-for="(item, index) in accordions" :key="index">
        <baseAccordion
          :id="item.key"
          :open="accordionsOpen[index]"
          header-padding-block="0.75rem"
          header-padding-inline="1.5rem"
          padding-block="0"
          padding-inline="0"
          @open="toggleAccordion(index)"
        >
          <template #title>
            <svg-icon :icon="item.icon" size="1.75rem" />
            {{ item.title }}
          </template>

          <template #content>
            <component
              v-if="item.component"
              :is="item.component"
              v-bind="item.props"
              @update-data="update()"
            />
          </template>
        </baseAccordion>
      </template>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.tab-component {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.15rem;
    font-weight: bold;
    text-transform: uppercase;
    border-bottom: 1px solid var(--border);
    padding-block-end: 0.25rem;
  }
}
</style>
